import { defineStore } from "pinia";
import { useAchievementsStore } from "@stores/achievements";

function roundUp3(n) {
    return Math.ceil(n * 1000) / 1000;
}

function preloadImage(src) {
    if (!src) return Promise.resolve();
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = src;
    });
}

function formatMinutesSeconds(ms) {
    const totalSec = Math.floor(ms / 1000);
    const m = String(Math.floor(totalSec / 60)).padStart(2, "0");
    const s = String(totalSec % 60).padStart(2, "0");
    return `${m}:${s}`;
}

function formatMs(ms) {
    return String(Math.floor(ms % 1000)).padStart(3, "0");
}

function formatFullTime(ms) {
    return `${formatMinutesSeconds(ms)}:${formatMs(ms)}`;
}

// Linear interpolation across a sorted table of { [xKey]: number, bonus: number }.
// Past the last point: returns the last bonus (speed table's final row is the floor).
// Before the first point: returns the first bonus.
function interpolateTable(table, xKey, x) {
    if (!table || table.length === 0) return 0;
    if (x <= table[0][xKey]) return table[0].bonus;
    for (let i = 0; i < table.length - 1; i++) {
        const a = table[i];
        const b = table[i + 1];
        if (x >= a[xKey] && x <= b[xKey]) {
            const span = b[xKey] - a[xKey];
            if (span === 0) return a.bonus;
            const t = (x - a[xKey]) / span;
            return Math.round(a.bonus + (b.bonus - a.bonus) * t);
        }
    }
    return table[table.length - 1].bonus;
}

function speedScore(table, timeMs) {
    // API table's `time` is centiseconds (time:200 ↔ 2s ↔ 9800).
    const centis = timeMs / 10;
    return interpolateTable(table, "time", centis);
}

function accuracyScore(table, guessCount) {
    if (!table || table.length === 0) return 0;
    // Direct lookup by guess index; past the last listed guess → 0.
    const row = table.find((r) => r.guess === guessCount);
    return row ? row.bonus : 0;
}

function computeRoundStats(scoreTables, guesses, timeMs) {
    const guessCount = guesses.length;
    const speedBonus = speedScore(scoreTables.speed, timeMs);
    const accuracyBonus = accuracyScore(scoreTables.accuracy, guessCount);
    return {
        time: formatFullTime(timeMs),
        timeMs,
        guesses,
        guessCount,
        speedBonus,
        accuracyBonus,
        roundPoints: speedBonus + accuracyBonus,
    };
}

const COUNTDOWN_START = 3;
const GO_DURATION_MS = 700;
const GET_READY_DURATION_MS = 1500;
const TIMER_TICK_MS = 50;
const TOTAL_ROUNDS = 5;

function emptyState() {
    return {
        gameState: "intro", // "intro" | "playing" | "results"
        photoPosition: "hidden", // "hidden" | "center" | "docked"
        getReady: false,
        countdown: 0,
        showGo: false,
        currentRound: 0,
        totalRounds: TOTAL_ROUNDS,
        currentQuestionId: null,
        currentQuestionImage: null,
        previousQuestionIds: [],
        roundGuesses: [],
        allRoundResults: [],
        lastFeedback: null, // { name, hex } | null
        timerMs: 0,
        leaderboardTab: "daily",
        resultsRoundView: 0,
        photoExpanded: false,
        scoreTables: { speed: [], accuracy: [] },
        gameBlob: {},
        loading: false,
        error: null,
    };
}

export const useTriviaStore = defineStore("trivia", {
    state: () => emptyState(),

    getters: {
        guessCount: (state) => state.roundGuesses.length,

        hasCorrectGuess: (state) => state.roundGuesses.some((g) => g.correct),

        isLastRound: (state) => state.currentRound >= state.totalRounds - 1,

        formattedTimer: (state) => formatMinutesSeconds(state.timerMs),

        formattedTimerMs: (state) => formatMs(state.timerMs),

        totalGuesses: (state) =>
            state.allRoundResults.reduce((sum, r) => sum + r.guesses.length, 0),

        totalPoints: (state) =>
            state.allRoundResults.reduce(
                (sum, r) =>
                    sum +
                    computeRoundStats(state.scoreTables, r.guesses, r.timeMs)
                        .roundPoints,
                0,
            ),

        allGuesses: (state) => state.allRoundResults.flatMap((r) => r.guesses),

        formattedTotalFullTime: (state) => {
            const totalMs = state.allRoundResults.reduce(
                (sum, r) => sum + r.timeMs,
                0,
            );
            return formatFullTime(totalMs);
        },

        roundStatsAt: (state) => (index) => {
            if (index < state.allRoundResults.length) {
                const r = state.allRoundResults[index];
                return computeRoundStats(
                    state.scoreTables,
                    r.guesses,
                    r.timeMs,
                );
            }
            if (index === state.currentRound && state.hasCorrectGuess) {
                return computeRoundStats(
                    state.scoreTables,
                    state.roundGuesses,
                    state.timerMs,
                );
            }
            return null;
        },

        currentRoundStats() {
            return this.roundStatsAt(this.currentRound);
        },
    },

    actions: {
        async startGame() {
            if (this.loading) return;
            this._clearTimers();
            Object.assign(this, emptyState());
            this.loading = true;
            try {
                const { data } = await axios.get("/api/trivia");
                this.scoreTables = data.bonuses ?? data;
                await this._loadNextQuestion();
                this._startRound();
            } catch (e) {
                console.error("Failed to start trivia game", e);
                this.error = "Could not start the game. Please try again.";
            } finally {
                this.loading = false;
            }
        },

        async nextRound() {
            if (this.loading) return;
            this._recordCurrentRound();
            this.currentRound++;
            this.loading = true;
            try {
                await this._loadNextQuestion();
                this._startRound();
            } catch (e) {
                console.error("Failed to load next round", e);
                this.error = "Could not load the next round.";
            } finally {
                this.loading = false;
            }
        },

        showResults() {
            if (this.gameState === "results") return;
            this._recordCurrentRound();
            this._clearTimers();
            this.gameState = "results";
            this.resultsRoundView = 0;
            useAchievementsStore().incrementProgress("game-grinder", 1);

            if (typeof window.gtag === "function") {
                window.gtag("event", "completed_trivia_game", {
                    guesses: this.totalGuesses,
                    time: this.formattedTotalFullTime,
                    score: this.totalPoints,
                });
            }
        },

        async submitGuess(country) {
            if (!country || !this.currentQuestionId) return;
            if (this.hasCorrectGuess) return;
            if (this.loading) return;

            const elapsed = roundUp3(this.timerMs / 1000);
            this.loading = true;
            try {
                const { data: res } = await axios.post("/api/trivia/guess", {
                    question: this.currentQuestionId,
                    guess: country.id,
                });

                const guess = {
                    countryId: country.id,
                    countryName: country.name,
                    distance: res.distance,
                    correct: res.correct,
                    temperatureName: res.temperature?.name ?? "",
                    temperatureHex: res.temperature?.hex ?? "",
                    timeSeconds: elapsed,
                };
                this.roundGuesses.push(guess);

                const blob = this.gameBlob[this.currentQuestionId];
                if (blob) {
                    blob.guesses.push({ id: country.id, time: elapsed });
                }

                this.lastFeedback = {
                    name: guess.temperatureName,
                    hex: guess.temperatureHex,
                };

                if (res.correct) {
                    clearInterval(this._timerInterval);
                    this.resultsRoundView = this.currentRound;
                    if (blob) blob.time = elapsed;
                }
            } catch (e) {
                console.error("Failed to submit guess", e);
                this.error = "Could not submit your guess.";
            } finally {
                this.loading = false;
            }
        },

        dockPhoto() {
            this.photoPosition = "docked";
        },

        togglePhotoExpanded() {
            this.photoExpanded = !this.photoExpanded;
        },

        setResultsRoundView(index) {
            this.resultsRoundView = index;
        },

        setLeaderboardTab(tab) {
            this.leaderboardTab = tab;
        },

        async submitScore({ username, email, marketing }) {
            await axios.post("/api/trivia/submit", {
                username,
                email,
                marketing: !!marketing,
                score: this.totalPoints,
                game: this.gameBlob,
            });

            if (typeof window.gtag === "function") {
                window.gtag("event", "submitted_trivia_score", {
                    score: this.totalPoints,
                });
            }
        },

        playAgain() {
            this._clearTimers();
            Object.assign(this, emptyState());
        },

        reset() {
            this._clearTimers();
            Object.assign(this, emptyState());
        },

        async _loadNextQuestion() {
            const prev = this.previousQuestionIds.length
                ? this.previousQuestionIds
                : null;
            const { data: q } = await axios.post("/api/trivia/question", {
                questions: prev,
            });
            this.currentQuestionId = q.id;
            this.currentQuestionImage = q.image;
            this.previousQuestionIds.push(q.id);
            this.gameBlob[q.id] = { time: 0, guesses: [] };
            await preloadImage(q.image);
        },

        _recordCurrentRound() {
            this.allRoundResults.push({
                questionId: this.currentQuestionId,
                image: this.currentQuestionImage,
                guesses: [...this.roundGuesses],
                timeMs: this.timerMs,
            });

            if (typeof window.gtag === "function") {
                const stats = computeRoundStats(
                    this.scoreTables,
                    this.roundGuesses,
                    this.timerMs,
                );
                window.gtag("event", "completed_trivia_round", {
                    guesses: stats.guessCount,
                    time: stats.time,
                    score: stats.roundPoints,
                });
            }
        },

        _startRound() {
            this._clearTimers();
            this.gameState = "playing";
            this.photoPosition = "hidden";
            this.roundGuesses = [];
            this.lastFeedback = null;
            this.timerMs = 0;
            this.countdown = 0;
            this.showGo = false;
            this.getReady = true;

            this._getReadyTimeout = setTimeout(() => {
                this.getReady = false;
                this.countdown = COUNTDOWN_START;

                this._countdownInterval = setInterval(() => {
                    this.countdown--;
                    if (this.countdown > 0) return;

                    clearInterval(this._countdownInterval);
                    this.showGo = true;
                    this._goTimeout = setTimeout(() => {
                        this.showGo = false;
                        this.dockPhoto();
                        this._startTimer();
                    }, GO_DURATION_MS);
                }, 1000);
            }, GET_READY_DURATION_MS);
        },

        _startTimer() {
            clearInterval(this._timerInterval);
            const start = performance.now();
            this._timerInterval = setInterval(() => {
                if (this.hasCorrectGuess) return;
                this.timerMs = performance.now() - start;
            }, TIMER_TICK_MS);
        },

        _clearTimers() {
            clearInterval(this._countdownInterval);
            clearInterval(this._timerInterval);
            clearTimeout(this._goTimeout);
            clearTimeout(this._getReadyTimeout);
        },
    },
});
